// =============================================================================
// SIREPPAR - Modal "Aprobar el reempadronamiento — Resolución del Secretario"
// =============================================================================
// El Secretario Electoral aprueba el reempadronamiento con su propia resolución.
// En lugar de pedirle número, fecha y un archivo firmado (como ModalResolucionTep,
// que siguen usando los actos del TEP), este modal le muestra el documento que se
// va a generar: la RESOLUCIÓN y, en hoja aparte, el CERTIFICADO, como
// hojas ya completas con los datos del partido y editables.
//
// - El membrete y el bloque de firma son fijos; todo el cuerpo se edita.
// - Herramientas: negrita, cursiva, lista y lista numerada (las del RichTextEditor).
// - Los campos que el sistema no pudo completar vienen resaltados en amarillo
//   ([___]); mientras quede alguno no se puede aprobar.
// - Papel: oficio (por defecto) o A4. Cambiarlo solo vuelve a cortar las hojas; lo
//   editado se conserva. El PDF y el Word salen en el mismo papel.
// - "Visualizar PDF" descarga el PDF tal como quedaría, sin cambiar nada.
// - "Descargar Word" descarga un borrador .docx del mismo texto (no se guarda).
// - "Aprobar y otorgar el reconocimiento" genera ese mismo PDF, lo descarga, lo
//   guarda en el expediente, lo manda por correo y aprueba.
//
// Los cuerpos editables NO se controlan con estado de React (el cursor saltaría
// en cada tecla): el HTML se carga una vez por apertura con refs y se lee al enviar.
//
// Las medidas de cada papel las manda el backend (las mismas del PDF), y la
// tipografía es la misma Liberation Serif embebida en el PDF, sin kerning. Los espacios y el
// tabulador son contenido (`white-space: pre-wrap`), y las hojas se cortan como
// en el PDF (ver utils/paginadoHoja).
// =============================================================================
import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DocumentArrowDownIcon, EyeIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import { api } from '../../services/api';
import {
  paginarDocumento,
  ptAPx,
  HUECO_ENTRE_HOJAS_PX,
  type GeometriaHoja,
} from '../../utils/paginadoHoja';

interface ModeloResolucion {
  resolucionHtml: string;
  anexoHtml: string;
  logoDataUrl: string | null;
  firmaDataUrl: string | null;
  geometria: Record<FormatoHoja, GeometriaHoja>;
  partido: { denominacion: string; sigla: string | null };
  expedienteNumero: string;
}

interface Props {
  abierto: boolean;
  expedienteId: string;
  onClose: () => void;
  onAprobado?: () => void;
}

/** Tope de archivos. Espeja MAX_ARCHIVOS_RESOLUCION del backend. */
const MAX_ARCHIVOS = 5;
const TIPOS_OK = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
/** Campos sin completar: [___], [__________], [NOMBRES Y DNI] o [IDENTIFICADOR]. */
const RE_MARCADOR = /\[_+\]|\[NOMBRES Y DNI\]|\[IDENTIFICADOR\]/g;
type FormatoHoja = 'LEGAL' | 'A4';
const FORMATOS: Array<{ valor: FormatoHoja; etiqueta: string }> = [
  { valor: 'LEGAL', etiqueta: 'Oficio / Legal (216 × 356 mm)' },
  { valor: 'A4', etiqueta: 'A4 (210 × 297 mm)' },
];
/** Familia de @font-face de la hoja (globals.css): los mismos archivos que usa el PDF. */
const FAMILIA_HOJA = 'SIREPPAR Liberation Serif';
const LEYENDA_ANIO = '2026: Año de la Innovación y Modernización del Estado de la Provincia del Chubut';
const FIRMA = { nombre: 'Alejandro Tullio', cargo: 'Secretario Electoral Permanente' };
type Documento = 'resolucion' | 'anexo';
const DOCUMENTOS: Documento[] = ['resolucion', 'anexo'];
const CONFIRMACION =
  '¿Aprobar el reempadronamiento y otorgar el reconocimiento?\n\n' +
  'Se genera la resolución en PDF (con el certificado) y se guarda en el expediente. ' +
  'El expediente pasa a "Vigente" y el partido también. El expediente NO se eleva al TEP.';

function descargarArchivo(base64: string, filename: string, mimeType: string) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export default function ModalAprobarReempSecretario({ abierto, expedienteId, onClose, onAprobado }: Props) {
  const resolucionRef = useRef<HTMLDivElement>(null);
  const anexoRef = useRef<HTMLDivElement>(null);
  // El HTML se vuelca a los editores UNA vez por apertura: un refetch posterior no
  // debe pisar lo que el Secretario ya editó.
  const cargadoRef = useRef(false);
  // Contenedor de las hojas, firma y <style> con los cortes, por documento.
  const contenedorRef = useRef<Record<Documento, HTMLDivElement | null>>({ resolucion: null, anexo: null });
  const firmaRef = useRef<Record<Documento, HTMLDivElement | null>>({ resolucion: null, anexo: null });
  const estiloRef = useRef<Record<Documento, HTMLStyleElement | null>>({ resolucion: null, anexo: null });
  const cuadroPendienteRef = useRef<number | null>(null);
  const [hojas, setHojas] = useState<Record<Documento, number>>({ resolucion: 1, anexo: 1 });

  const [faltan, setFaltan] = useState(0);
  const [archivos, setArchivos] = useState<File[]>([]);
  const [avisarPartido, setAvisarPartido] = useState(true);
  const [enviando, setEnviando] = useState<null | 'vista' | 'word' | 'aprobar'>(null);
  const [formato, setFormato] = useState<FormatoHoja>('LEGAL');
  // El paginado corre en un requestAnimationFrame: lee el papel vigente de acá.
  const formatoRef = useRef<FormatoHoja>('LEGAL');
  formatoRef.current = formato;

  const { data: modelo, isFetching, error, refetch } = useQuery({
    queryKey: ['resolucion-secretario-reemp-modelo', expedienteId],
    queryFn: async () => {
      const res = await api.get(`/reempadronamiento/${expedienteId}/resolucion-secretario/modelo`);
      return res.data.data as ModeloResolucion;
    },
    enabled: abierto,
    // Siempre el modelo del día (la fecha va en el texto) y sin cache entre aperturas.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  /**
   * Cuenta los campos sin completar (por TEXTO, no por las marcas) y apaga el
   * resaltado de los que ya se completaron. Se cambia solo la clase, no el DOM,
   * para no mover el cursor.
   */
  const actualizarFaltantes = () => {
    let total = 0;
    for (const el of [resolucionRef.current, anexoRef.current]) {
      if (!el) continue;
      total += (el.innerText.match(RE_MARCADOR) || []).length;
      el.querySelectorAll<HTMLElement>('span[data-campo]').forEach((span) => {
        span.classList.toggle('falta', new RegExp(RE_MARCADOR.source).test(span.textContent || ''));
      });
      // Los espejos se completan solos: no son un campo más para el Secretario.
      el.querySelectorAll<HTMLElement>('[data-espejo]').forEach((span) => {
        total -= (span.textContent?.match(RE_MARCADOR) || []).length;
      });
    }
    setFaltan(total);
  };

  /**
   * Copia el N° escrito en el título de la resolución ("RESOLUCIÓN N° 12/2026") al
   * certificado. Se busca en MAYÚSCULAS, igual que el backend, para no tomar las
   * citas del cuerpo ("Resolución N° 2/2026 ISEP"). El espejo no es editable, así
   * que cambiarle el texto no mueve el cursor.
   */
  const sincronizarNroResolucion = () => {
    const texto = resolucionRef.current?.innerText ?? '';
    const nro = texto.match(/RESOLUCI[OÓ]N\s*N\s*\.?\s*[°º]?\s*([^\s/]*)\s*\//)?.[1]?.trim();
    copiarEnEspejos('nroResolucion', nro || '[___]', '[___]');
  };

  /**
   * Copia el identificador del partido que el Secretario escribió en el Considerando
   * ("el partido "X", identificador <acá>, de ámbito…") a sus espejos: Art. 1°,
   * Art. 2° y el certificado. Es lo que el backend guarda al aprobar, con la misma
   * regla: lo que va después de "identificador " hasta la coma.
   */
  const sincronizarIdentificador = () => {
    const texto = resolucionRef.current?.innerText ?? '';
    const valor = texto.match(/identificador\s+([^,]*?)\s*,/)?.[1]?.trim();
    copiarEnEspejos('identificador', valor || '[IDENTIFICADOR]', '[IDENTIFICADOR]');
  };

  /** Escribe `valor` en los espejos de los dos cuerpos; resaltados mientras sean el marcador. */
  const copiarEnEspejos = (tipo: 'nroResolucion' | 'identificador', valor: string, marcador: string) => {
    for (const el of [resolucionRef.current, anexoRef.current]) {
      el?.querySelectorAll<HTMLElement>(`[data-espejo="${tipo}"]`).forEach((span) => {
        if (span.textContent !== valor) span.textContent = valor;
        span.classList.toggle('falta', valor === marcador);
      });
    }
  };

  const sincronizarEspejos = () => {
    sincronizarNroResolucion();
    sincronizarIdentificador();
  };

  /** Recalcula dónde corta cada hoja (en el próximo cuadro, una sola vez). */
  const programarPaginado = () => {
    if (cuadroPendienteRef.current !== null) return;
    cuadroPendienteRef.current = requestAnimationFrame(() => {
      cuadroPendienteRef.current = null;
      if (!modelo) return;
      const nuevas: Record<Documento, number> = { resolucion: 1, anexo: 1 };
      for (const doc of DOCUMENTOS) {
        const contenedor = contenedorRef.current[doc];
        const cuerpo = doc === 'resolucion' ? resolucionRef.current : anexoRef.current;
        const estilo = estiloRef.current[doc];
        if (!contenedor || !cuerpo || !estilo) continue;
        nuevas[doc] = paginarDocumento({
          contenedor,
          cuerpo,
          firma: firmaRef.current[doc],
          estilo,
          selectorCuerpo: `[data-cuerpo="${doc}"]`,
          selectorFirma: `[data-firma="${doc}"]`,
          geo: modelo.geometria[formatoRef.current],
        });
      }
      setHojas((prev) => (prev.resolucion === nuevas.resolucion && prev.anexo === nuevas.anexo ? prev : nuevas));
    });
  };

  const alEditar = () => {
    sincronizarEspejos();
    actualizarFaltantes();
    programarPaginado();
  };

  useEffect(() => () => {
    if (cuadroPendienteRef.current !== null) cancelAnimationFrame(cuadroPendienteRef.current);
  }, []);

  useEffect(() => {
    if (!abierto) {
      cargadoRef.current = false;
      return;
    }
    if (!modelo || isFetching || cargadoRef.current || !resolucionRef.current || !anexoRef.current) return;
    resolucionRef.current.innerHTML = modelo.resolucionHtml;
    anexoRef.current.innerHTML = modelo.anexoHtml;
    // Las marcas del modelo se identifican para poder apagarlas cuando se completan.
    [resolucionRef.current, anexoRef.current].forEach((el) =>
      el.querySelectorAll('span.falta').forEach((s) => s.setAttribute('data-campo', '')),
    );
    cargadoRef.current = true;
    sincronizarEspejos();
    actualizarFaltantes();
    programarPaginado();
    // Las medidas cambian cuando termina de cargar la tipografía: se piden las cuatro
    // variantes y se vuelve a cortar cuando están.
    const fuentes = document.fonts;
    if (fuentes) {
      Promise.all(
        ['normal 400', 'normal 700', 'italic 400', 'italic 700'].map((v) => fuentes.load(`${v} 12pt "${FAMILIA_HOJA}"`)),
      )
        .then(() => fuentes.ready)
        .then(() => programarPaginado())
        .catch(() => programarPaginado());
    }
  });

  // Cambiar de papel no toca el contenido: solo se vuelven a cortar las hojas.
  useEffect(() => {
    if (cargadoRef.current) programarPaginado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formato]);

  useEffect(() => {
    if (abierto) {
      setArchivos([]);
      setAvisarPartido(true);
      setFaltan(0);
      setFormato('LEGAL');
    }
  }, [abierto]);

  const cerrar = () => {
    if (enviando) return;
    cargadoRef.current = false;
    onClose();
  };

  // Herramientas del editor. onMouseDown con preventDefault: el foco y la selección
  // se quedan en la hoja, que es donde actúa execCommand.
  const comando = (cmd: 'bold' | 'italic' | 'insertUnorderedList' | 'insertOrderedList') => {
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(cmd, false);
    alEditar();
  };

  // Tab escribe un tabulador (en lugar de mover el foco fuera de la hoja), para poder
  // sangrar en cualquier lugar. Shift+Tab sigue saliendo de la hoja.
  const teclaEnHoja = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      document.execCommand('insertText', false, '\t');
    }
  };

  // Se pega solo texto: el formato de Word o de una página web traería estilos que
  // el PDF no reproduce.
  const pegarTexto = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const texto = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, texto);
  };
  const bloquearArrastre = (e: DragEvent<HTMLDivElement>) => e.preventDefault();

  const agregarArchivos = (nuevos: FileList | null) => {
    if (!nuevos || nuevos.length === 0) return;
    const validos: File[] = [];
    for (const f of Array.from(nuevos)) {
      if (!TIPOS_OK.includes(f.type) && !/\.(pdf|jpe?g|png|webp)$/i.test(f.name)) {
        toast.error(`"${f.name}" no es un PDF ni una imagen.`);
        continue;
      }
      validos.push(f);
    }
    setArchivos((prev) => {
      const total = [...prev, ...validos];
      if (total.length > MAX_ARCHIVOS) {
        toast.error(`Podés adjuntar hasta ${MAX_ARCHIVOS} archivos.`);
        return total.slice(0, MAX_ARCHIVOS);
      }
      return total;
    });
  };
  const quitarArchivo = (idx: number) => setArchivos((p) => p.filter((_, i) => i !== idx));

  /** Vista previa en PDF, borrador en Word, o la aprobación (PDF que se guarda). */
  const enviar = async (modo: 'vista' | 'word' | 'aprobar') => {
    const persistir = modo === 'aprobar';
    const resolucionHtml = resolucionRef.current?.innerHTML ?? '';
    const anexoHtml = anexoRef.current?.innerHTML ?? '';
    if (!resolucionRef.current?.innerText.trim() || !anexoRef.current?.innerText.trim()) {
      toast.error('La resolución y el certificado no pueden quedar vacíos.');
      return;
    }
    if (persistir) {
      if (faltan > 0) {
        toast.error('Completá los campos resaltados en amarillo antes de aprobar.');
        return;
      }
      if (!window.confirm(CONFIRMACION)) return;
    }

    const fd = new FormData();
    fd.append('resolucionHtml', resolucionHtml);
    fd.append('anexoHtml', anexoHtml);
    fd.append('persistir', String(persistir));
    fd.append('notificarPartido', String(avisarPartido));
    fd.append('formato', formato);
    fd.append('salida', modo === 'word' ? 'docx' : 'pdf');
    // La vista previa no necesita los archivos: no se guardan ni se mandan.
    if (persistir) archivos.forEach((f) => fd.append('archivos', f));

    setEnviando(modo);
    try {
      const res = await api.post(`/reempadronamiento/${expedienteId}/aprobar-secretario`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Genera el PDF y, al aprobar, sube los archivos: más que los 30 s por defecto.
        timeout: 120000,
      });
      const { archivoBase64, filename, mimeType } = res.data.data;
      descargarArchivo(archivoBase64, filename, mimeType);
      if (persistir) {
        toast.success('Reempadronamiento aprobado');
        onAprobado?.();
        cargadoRef.current = false;
        onClose();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || (
        modo === 'aprobar' ? 'Error al aprobar el reempadronamiento'
          : modo === 'word' ? 'Error al generar el Word'
            : 'Error al generar la vista previa del PDF'));
    } finally {
      setEnviando(null);
    }
  };

  const descripcion = modelo
    ? `${modelo.partido.denominacion}` +
      ``
    : '';
  // Al reabrir, se espera el modelo fresco antes de mostrar las hojas: el que quedó
  // de la apertura anterior podría tener otra fecha.
  const preparando = isFetching && !cargadoRef.current;
  const listo = !!modelo && !error && !preparando;

  /**
   * Un documento: los fondos de hoja (con su membrete) detrás, y encima el cuerpo
   * editable y la firma, que fluyen de corrido. Los cortes los pone el <style>.
   */
  const renderDocumento = (doc: Documento) => {
    if (!modelo) return null;
    const g = modelo.geometria[formato];
    const alto = ptAPx(g.altoPt);
    const n = hojas[doc];
    return (
      <div
        ref={(el) => { contenedorRef.current[doc] = el; }}
        className="hoja-resolucion"
        style={{
          width: ptAPx(g.anchoPt),
          minHeight: n * alto + (n - 1) * HUECO_ENTRE_HOJAS_PX,
          paddingTop: ptAPx(g.cuerpoTopPt),
          paddingLeft: ptAPx(g.margenIzqPt),
          paddingRight: ptAPx(g.margenDerPt),
        }}
      >
        <style ref={(el) => { estiloRef.current[doc] = el; }} />
        {Array.from({ length: n }, (_, i) => (
          <div
            key={i}
            className="pagina"
            aria-hidden={i > 0}
            contentEditable={false}
            style={{ top: i * (alto + HUECO_ENTRE_HOJAS_PX), height: alto }}
          >
            <div
              className="anio"
              style={{
                top: ptAPx(g.membreteSupPt),
                left: ptAPx(g.margenIzqPt),
                right: ptAPx(g.margenDerPt),
                height: ptAPx(g.altoLeyendaPt),
                lineHeight: `${ptAPx(g.altoLeyendaPt)}px`,
              }}
            >
              {LEYENDA_ANIO}
            </div>
            {modelo.logoDataUrl && (
              <img
                src={modelo.logoDataUrl}
                alt={i === 0 ? 'Secretaría Electoral Permanente' : ''}
                style={{
                  top: ptAPx(g.membreteSupPt + g.altoLeyendaPt + g.separacionLeyendaLogoPt),
                  left: ptAPx(g.margenIzqPt),
                  width: ptAPx(g.anchoLogoPt),
                  height: ptAPx(g.altoLogoPt),
                }}
              />
            )}
          </div>
        ))}
        <div
          ref={doc === 'resolucion' ? resolucionRef : anexoRef}
          className="cuerpo"
          data-cuerpo={doc}
          contentEditable={!enviando}
          suppressContentEditableWarning
          spellCheck
          lang="es"
          onInput={alEditar}
          onKeyDown={teclaEnHoja}
          onPaste={pegarTexto}
          onDrop={bloquearArrastre}
        />
        <div ref={(el) => { firmaRef.current[doc] = el; }} className="firma" data-firma={doc} contentEditable={false}>
          {modelo.firmaDataUrl && <img src={modelo.firmaDataUrl} alt="" />}
          <div><b>{FIRMA.nombre}</b></div>
          <div>{FIRMA.cargo}</div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={abierto}
      onClose={cerrar}
      title="Aprobar el reempadronamiento — Resolución del Secretario"
      description={descripcion}
      size="hoja"
      compacto
      // Un click afuera cerraría el modal y se perdería lo editado.
      closeOnOverlayClick={false}
    >
      <div className="-mx-6 -my-6">
        {/* Barra de herramientas: las mismas del editor de informes */}
        <div className="flex items-center gap-1 px-6 py-2 border-b bg-gray-50 text-gray-700">
          <button
            type="button"
            className="w-8 h-8 rounded hover:bg-gray-200 font-bold disabled:opacity-40"
            title="Negrita"
            disabled={!listo}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => comando('bold')}
          >
            B
          </button>
          <button
            type="button"
            className="w-8 h-8 rounded hover:bg-gray-200 italic font-serif disabled:opacity-40"
            title="Cursiva"
            disabled={!listo}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => comando('italic')}
          >
            I
          </button>
          <span className="w-px h-5 bg-gray-300 mx-1" />
          <button
            type="button"
            className="w-8 h-8 rounded hover:bg-gray-200 disabled:opacity-40"
            title="Lista"
            disabled={!listo}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => comando('insertUnorderedList')}
          >
            <svg className="w-4 h-4 mx-auto" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="2" cy="4" r="1.3" /><circle cx="2" cy="8" r="1.3" /><circle cx="2" cy="12" r="1.3" />
              <rect x="5" y="3.3" width="10" height="1.4" /><rect x="5" y="7.3" width="10" height="1.4" />
              <rect x="5" y="11.3" width="10" height="1.4" />
            </svg>
          </button>
          <button
            type="button"
            className="w-8 h-8 rounded hover:bg-gray-200 text-xs font-semibold disabled:opacity-40"
            title="Lista numerada"
            disabled={!listo}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => comando('insertOrderedList')}
          >
            1.
          </button>
          <span className="w-px h-5 bg-gray-300 mx-1" />
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            Papel
            <select
              value={formato}
              onChange={(e) => setFormato(e.target.value as FormatoHoja)}
              disabled={!listo || !!enviando}
              className="border border-gray-300 rounded-md py-1 pl-2 pr-7 text-xs bg-white disabled:opacity-50"
            >
              {FORMATOS.map((f) => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
            </select>
          </label>
          {listo && (faltan > 0 ? (
            <span className="ml-auto text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
              Faltan completar <b>{faltan}</b> {faltan === 1 ? 'campo' : 'campos'} <span className="marcador-falta">[___]</span>
            </span>
          ) : (
            <span className="ml-auto text-xs text-green-800 bg-green-50 border border-green-200 rounded-md px-2 py-1">
              Todos los campos están completos
            </span>
          ))}
        </div>

        {/* Las hojas */}
        <div className="h-[calc(100vh-13rem)] min-h-[18rem] overflow-auto bg-gray-100 px-6 py-6">
          {preparando && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500 text-sm">
              <Spinner className="h-8 w-8 text-blue-600" />
              Preparando la resolución…
            </div>
          )}
          {error && !preparando && (
            <div className="max-w-md mx-auto mt-10 text-center text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              <p className="font-medium">No se pudo cargar el modelo de la resolución.</p>
              <p className="mt-1">
                {(error as any)?.response?.data?.error?.message || 'Intentá de nuevo en unos segundos.'}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-3 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium"
              >
                Reintentar
              </button>
            </div>
          )}
          {listo && (
            <>
              {renderDocumento('resolucion')}
              <div className="text-center text-[11px] text-gray-500 my-3.5 tracking-wider">— CERTIFICADO · HOJA APARTE —</div>
              {renderDocumento('anexo')}
            </>
          )}
        </div>

        <div className="border-t px-6 py-2.5 bg-white">
          {archivos.length > 0 && (
            <ul className="mb-2 flex flex-wrap gap-1.5">
              {archivos.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-1.5 text-xs bg-gray-50 border rounded px-2 py-0.5 max-w-[16rem]">
                  <span className="truncate">({i + 1}) {f.name}</span>
                  <button
                    type="button"
                    onClick={() => quitarArchivo(i)}
                    className="text-red-600 hover:text-red-700 shrink-0"
                    title="Quitar"
                    disabled={!!enviando}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <label
              className={`inline-flex items-center gap-1.5 text-sm text-gray-700 ${
                archivos.length >= MAX_ARCHIVOS || enviando ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:text-gray-900'
              }`}
              title={`Opcional: PDF o imagen, hasta ${MAX_ARCHIVOS}. Viaja aparte en el correo y queda como "Archivo adjunto a la Resolución N°…".`}
            >
              <PaperClipIcon className="h-4 w-4" />
              Adjuntar archivo
              <input
                type="file"
                multiple
                accept="application/pdf,.pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => { agregarArchivos(e.target.files); e.target.value = ''; }}
                className="sr-only"
                disabled={archivos.length >= MAX_ARCHIVOS || !!enviando}
              />
            </label>
            <label
              className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer"
              title="Se envía esta resolución en PDF (con el certificado anexo) al partido y a todos sus apoderados."
            >
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={avisarPartido}
                onChange={(e) => setAvisarPartido(e.target.checked)}
                disabled={!!enviando}
              />
              Notificar al partido por correo
            </label>
            <div className="flex flex-wrap justify-end gap-2 ml-auto">
              <button
                type="button"
                onClick={cerrar}
                disabled={!!enviando}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => enviar('word')}
                disabled={!listo || !!enviando}
                title="Descarga un borrador en Word del texto de la hoja. No se guarda ni se envía."
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {enviando === 'word' ? <Spinner /> : <DocumentArrowDownIcon className="h-4 w-4" />}
                Descargar Word
              </button>
              <button
                type="button"
                onClick={() => enviar('vista')}
                disabled={!listo || !!enviando}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {enviando === 'vista' ? <Spinner /> : <EyeIcon className="h-4 w-4" />}
                Visualizar PDF
              </button>
              
              <button
                type="button"
                onClick={() => enviar('aprobar')}
                disabled={!listo || !!enviando || faltan > 0}
                title={faltan > 0 ? 'Completá los campos resaltados en amarillo para poder aprobar.' : undefined}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enviando === 'aprobar' && <Spinner />}
                {enviando === 'aprobar' ? 'Aprobando…' : 'Aprobar y otorgar el reconocimiento'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
